import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import PublicNav from '../components/PublicNav';
import MarketingFooter from '../components/MarketingFooter';
import { Motif } from '../components/ui/Motif';
import { Reveal, RevealGroup } from '../components/ui/Reveal';

const BG = '#F5FAFC';
const INK = '#002A38';
const MUTED = '#78716C';
const TERRA = '#0076A3';
const LINE = '#ECE7DE';

const ACCENTS = [
  { tile: '#FFE7D9', ink: '#0076A3' },
  { tile: '#E6F0E7', ink: '#5B8C63' },
  { tile: '#FDEFD7', ink: '#D08A2C' },
  { tile: '#F1E4F5', ink: '#9A5BB0' },
];

const overline = { fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: TERRA, marginBottom: '0.75rem' };
const h2 = { fontFamily: 'Cabinet Grotesk', fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.06, marginBottom: '1rem' };

const STAGES = [
  { n: '01', t: 'Screen', d: 'JD-to-resume matching, candidate scoring, skill-gap identification, business-defined thresholds and candidate prioritization.', icon: '🎯' },
  { n: '02', t: 'Assess', d: 'AI-generated assessments, automated distribution, completion tracking, follow-ups and result collection.', icon: '📝' },
  { n: '03', t: 'Interview', d: 'SME validation, structured AI-assisted interviews, technical and behavioural evaluation, interview insights.', icon: '🎥' },
  { n: '04', t: 'Progress', d: 'Candidate evidence, human validation, pipeline stage progression, pending actions and next-step recommendations.', icon: '🚀' },
];

const EFFORT = [
  { role: 'Recruiters / TA', hrs: '~600 hrs', work: 'Screening, assessments, follow-ups, consolidation' },
  { role: 'SMEs / Technical Experts', hrs: '~133 hrs', work: 'Validation, scoring, technical evaluation' },
  { role: 'Hiring Managers', hrs: '~50 hrs', work: 'Candidate review, feedback, hiring decisions' },
];

const RESULTS = [
  { v: '2,000', l: 'Resumes processed and evaluated' },
  { v: '48 hrs', l: 'End-to-end initial screening cycle' },
  { v: '~90%', l: 'Reduction in initial screening cycle time' },
  { v: '2,000', l: 'Candidates manually validated' },
  { v: '85%', l: 'Business-defined match benchmark' },
  { v: 'PII', l: 'Identifying data excluded from evaluation' },
];

const FUNNEL = [
  { stage: 'Resumes Received', count: 2000, pct: '100%' },
  { stage: 'Resume Shortlist', count: 420, pct: '21%' },
  { stage: 'Assessments', count: 180, pct: '9%' },
  { stage: 'Interviews', count: 64, pct: '3.2%' },
  { stage: 'Background Verification', count: 22, pct: '1.1%' },
  { stage: 'Offers', count: 12, pct: '0.6%' },
];
const FUNNEL_W = [100, 72, 54, 40, 30, 22];
const FUNNEL_C = ['#F3B5A4', '#EC8A76', '#0076A3', '#D65F4C', '#005A80', '#A8442F'];

export default function CaseStudyPage() {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div style={{ minHeight: '100vh', background: BG, color: INK, overflowX: 'hidden' }} data-testid="case-study-page">
      <PublicNav />

      {/* ─── Hero ─── */}
      <section style={{ position: 'relative', padding: 'clamp(3rem, 6vw, 5.5rem) clamp(1.25rem, 5vw, 5rem)', maxWidth: 1160, margin: '0 auto' }}>
        <div className="grain-overlay" style={{ opacity: 0.16 }} />
        <Motif shape="star" className="motif-float" style={{ position: 'absolute', top: '14%', right: '7%', width: 44, color: TERRA, opacity: 0.8 }} />
        <Motif shape="ring" className="motif-float" stroke style={{ position: 'absolute', bottom: '8%', left: '3%', width: 56, color: '#A9DCF2', opacity: 0.5, animationDelay: '0.7s' }} />
        <div style={{ position: 'relative', zIndex: 1, maxWidth: 820 }}>
          <Reveal now><div style={overline}>Case Study · Global IT Services Enterprise</div></Reveal>
          <Reveal now delay={0.06}>
            <h1 style={{ fontFamily: 'Cabinet Grotesk', fontSize: 'clamp(2.25rem, 5.5vw, 4rem)', fontWeight: 800, lineHeight: 1.03, letterSpacing: '-0.03em', marginBottom: '1.25rem' }}>
              2,000 resumes screened in <span style={{ color: TERRA }}>48 hours</span>.
            </h1>
          </Reveal>
          <Reveal now delay={0.14}>
            <p style={{ fontSize: 'clamp(1.05rem, 1.5vw, 1.2rem)', color: MUTED, lineHeight: 1.65, maxWidth: 720 }}>
              A leading global IT services firm ran a large-scale hiring evaluation — roughly 20 recruiters across multiple sourcing channels. CHIRE <strong style={{ color: INK }}>HIRE</strong> — the Hiring Intelligence &amp; Recruitment Engine — compressed an initial screening cycle that previously took about a month down to 48 hours, without giving up human judgment or control.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── Headline metrics ─── */}
      <section style={{ maxWidth: 1160, margin: '0 auto', padding: '0 clamp(1.25rem, 4vw, 3rem) clamp(2rem, 4vw, 3.5rem)' }}>
        <RevealGroup stagger={0.06} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
          {[
            { v: '~1 mo → 48 hrs', l: 'Screening cycle' },
            { v: '~90%', l: 'Cycle-time reduction' },
            { v: '~783', l: 'Person-hours of modeled effort' },
            { v: '~98', l: '8-hour person-days' },
          ].map((m) => (
            <Reveal key={m.l}>
              <div style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 20, padding: '1.5rem', height: '100%' }}>
                <div style={{ fontFamily: 'Cabinet Grotesk', fontSize: 'clamp(1.4rem, 2.4vw, 1.9rem)', fontWeight: 800, color: TERRA, lineHeight: 1 }}>{m.v}</div>
                <div style={{ fontSize: '0.8125rem', color: MUTED, marginTop: '0.5rem' }}>{m.l}</div>
              </div>
            </Reveal>
          ))}
        </RevealGroup>
      </section>

      {/* ─── The Challenge ─── */}
      <section style={{ background: '#fff', borderTop: `1px solid ${LINE}`, borderBottom: `1px solid ${LINE}` }}>
        <div style={{ maxWidth: 1160, margin: '0 auto', padding: 'clamp(3rem, 6vw, 5rem) clamp(1.25rem, 4vw, 3rem)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 'clamp(2rem, 4vw, 4rem)', alignItems: 'center' }}>
            <Reveal>
              <div>
                <div style={overline}>The Challenge</div>
                <h2 style={h2}>High volume, high coordination, high hidden cost.</h2>
                <p style={{ fontSize: '1rem', color: MUTED, lineHeight: 1.7 }}>
                  2,000 resumes required significant recruiter and stakeholder effort across screening, shortlisting, assessments, follow-ups, result consolidation, SME validation and candidate progression. Candidate information was fragmented across resumes, emails, spreadsheets and separate tools — and the real cost wasn&apos;t just recruitment spend, it was the professional capacity consumed to process, coordinate and evaluate every candidate.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div style={{ background: BG, border: `1px solid ${LINE}`, borderRadius: 22, padding: '1.75rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: TERRA, marginBottom: '1rem' }}>The Opportunity</div>
                {['Reduce repetitive processing', 'Connect candidate evidence', 'Accelerate evaluation', 'Focus humans on judgment'].map((o) => (
                  <div key={o} style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', padding: '0.55rem 0', fontSize: '0.9375rem', color: '#44403C' }}>
                    <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: 999, background: '#FFE7D9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={TERRA} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    </span>
                    {o}
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ─── What HIRE did ─── */}
      <section style={{ maxWidth: 1160, margin: '0 auto', padding: 'clamp(3rem, 6vw, 5rem) clamp(1.25rem, 4vw, 3rem)' }}>
        <Reveal>
          <div style={{ maxWidth: 680, marginBottom: '2.75rem' }}>
            <div style={overline}>What CHIRE HIRE Did</div>
            <h2 style={h2}>One engine across the candidate evaluation journey.</h2>
            <p style={{ fontSize: '1.0625rem', color: MUTED, lineHeight: 1.65 }}>AI handled the volume — processing, evaluation, ranking and workflow coordination. HIRE organized the evidence into one connected workflow, while recruiters, SMEs and hiring managers retained the judgment on every consequential decision.</p>
          </div>
        </Reveal>
        <RevealGroup stagger={0.07} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.5rem' }}>
          {STAGES.map((s, i) => {
            const a = ACCENTS[i % ACCENTS.length];
            return (
              <Reveal key={s.t}>
                <div style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 22, padding: '1.75rem', height: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.1rem' }}>
                    <div style={{ width: 52, height: 52, borderRadius: 15, background: a.tile, color: a.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem' }}>{s.icon}</div>
                    <span style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.6rem', fontWeight: 800, color: LINE }}>{s.n}</span>
                  </div>
                  <h4 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.25rem', fontWeight: 800, marginBottom: '0.5rem' }}>{s.t}</h4>
                  <p style={{ fontSize: '0.875rem', color: MUTED, lineHeight: 1.65 }}>{s.d}</p>
                </div>
              </Reveal>
            );
          })}
        </RevealGroup>
        <Reveal delay={0.1}>
          <div style={{ marginTop: '1.75rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1.25rem', background: '#fff', border: `1px solid ${LINE}`, borderRadius: 18, padding: '1.1rem 1.5rem', fontSize: '0.9rem', color: '#44403C', fontWeight: 600 }}>
            <span style={{ color: TERRA, fontWeight: 800 }}>Trust &amp; Verification:</span>
            <span>🛡️ Verified Credentials</span>
            <span>✅ BGV Screening</span>
            <span>🔍 Candidate Evidence Auditability</span>
          </div>
        </Reveal>
      </section>

      {/* ─── Hiring funnel ─── */}
      <section style={{ background: '#fff', borderTop: `1px solid ${LINE}`, borderBottom: `1px solid ${LINE}` }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: 'clamp(3rem, 6vw, 5rem) clamp(1.25rem, 4vw, 3rem)' }}>
          <Reveal>
            <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 2.75rem' }}>
              <div style={{ ...overline, textAlign: 'center' }}>The Hiring Funnel</div>
              <h2 style={h2}>From 2,000 resumes to a confident offer.</h2>
              <p style={{ fontSize: '1rem', color: MUTED, lineHeight: 1.65 }}>CHIRE HIRE carries every candidate through one connected pipeline — screening, assessment, interview, verification and offer — with human validation at every stage.</p>
            </div>
          </Reveal>
          <RevealGroup stagger={0.08}>
            {FUNNEL.map((f, i) => (
              <Reveal key={f.stage}>
                <div style={{ width: `${FUNNEL_W[i]}%`, minWidth: 240, maxWidth: '100%', margin: '0 auto 0.7rem' }}>
                  <div style={{ background: FUNNEL_C[i], borderRadius: 14, padding: '0.95rem 1.5rem', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', boxShadow: '0 12px 26px -16px rgba(28,25,23,0.45)' }}>
                    <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>{f.stage}</span>
                    <span style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexShrink: 0 }}>
                      <span style={{ fontFamily: 'Cabinet Grotesk', fontWeight: 800, fontSize: '1.3rem', lineHeight: 1 }}>{f.count.toLocaleString()}</span>
                      <span style={{ fontSize: '0.72rem', opacity: 0.85 }}>{f.pct}</span>
                    </span>
                  </div>
                </div>
              </Reveal>
            ))}
          </RevealGroup>
          <Reveal delay={0.1}>
            <p style={{ marginTop: '1.5rem', textAlign: 'center', fontSize: '0.78rem', color: '#A8A29E' }}>Illustrative funnel for the 2,000-resume evaluation; downstream stage counts are representative.</p>
          </Reveal>
        </div>
      </section>

      {/* ─── Modeled effort ─── */}
      <section style={{ background: '#fff', borderTop: `1px solid ${LINE}`, borderBottom: `1px solid ${LINE}` }}>
        <div style={{ maxWidth: 1160, margin: '0 auto', padding: 'clamp(3rem, 6vw, 5rem) clamp(1.25rem, 4vw, 3rem)' }}>
          <Reveal>
            <div style={{ maxWidth: 680, marginBottom: '2.25rem' }}>
              <div style={overline}>From Recruiter Effort to Hiring Capacity</div>
              <h2 style={h2}>~783 person-hours of modeled human effort — released back to the team.</h2>
              <p style={{ fontSize: '1rem', color: MUTED, lineHeight: 1.65 }}>Illustrative model for the 2,000-resume use case. Actual savings vary by workflow and stakeholder involvement.</p>
            </div>
          </Reveal>
          <RevealGroup stagger={0.06} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.25rem', marginBottom: '1.25rem' }}>
            {EFFORT.map((e) => (
              <Reveal key={e.role}>
                <div style={{ background: BG, border: `1px solid ${LINE}`, borderRadius: 20, padding: '1.6rem', height: '100%' }}>
                  <div style={{ fontFamily: 'Cabinet Grotesk', fontSize: '2rem', fontWeight: 800, color: INK, lineHeight: 1 }}>{e.hrs}</div>
                  <div style={{ fontWeight: 700, fontSize: '0.9375rem', marginTop: '0.6rem' }}>{e.role}</div>
                  <div style={{ fontSize: '0.8125rem', color: MUTED, marginTop: '0.25rem', lineHeight: 1.5 }}>{e.work}</div>
                </div>
              </Reveal>
            ))}
          </RevealGroup>
          <Reveal>
            <div style={{ background: INK, borderRadius: 20, padding: '1.5rem 1.75rem', display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', gap: '0.75rem', color: '#fff' }}>
              <span style={{ fontSize: '0.9375rem', color: 'rgba(255,255,255,0.72)' }}>Total aggregate modeled human effort</span>
              <span style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.75rem', fontWeight: 800, color: '#FAD9A0' }}>~783 hrs&nbsp;&nbsp;·&nbsp;&nbsp;~98 person-days</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─── Results ─── */}
      <section style={{ maxWidth: 1160, margin: '0 auto', padding: 'clamp(3rem, 6vw, 5rem) clamp(1.25rem, 4vw, 3rem)' }}>
        <Reveal>
          <div style={{ maxWidth: 680, marginBottom: '2.5rem' }}>
            <div style={overline}>Results &amp; Quality Control</div>
            <h2 style={h2}>Faster — and validated.</h2>
            <p style={{ fontSize: '1.0625rem', color: MUTED, lineHeight: 1.65 }}>Speed only counts if quality holds. Every one of the 2,000 candidates was manually validated against an 85% business-defined benchmark, and no obviously unsuitable candidate was highly ranked during validation.</p>
          </div>
        </Reveal>
        <RevealGroup stagger={0.05} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem' }}>
          {RESULTS.map((r) => (
            <Reveal key={r.l}>
              <div style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 20, padding: '1.6rem', height: '100%' }}>
                <div style={{ fontFamily: 'Cabinet Grotesk', fontSize: '2rem', fontWeight: 800, color: TERRA, lineHeight: 1 }}>{r.v}</div>
                <div style={{ fontSize: '0.875rem', color: MUTED, marginTop: '0.6rem', lineHeight: 1.5 }}>{r.l}</div>
              </div>
            </Reveal>
          ))}
        </RevealGroup>
        <Reveal delay={0.1}>
          <p style={{ marginTop: '2rem', fontSize: '0.8125rem', color: '#A8A29E', lineHeight: 1.7, maxWidth: 860 }}>
            <strong style={{ color: MUTED }}>Method &amp; constraints:</strong> ATS connectivity was not yet enabled for this engagement, so resumes were uploaded manually in batches of ~100. Effort and person-hour figures are modeled and illustrative for the 2,000-resume use case and will vary by workflow and stakeholder involvement. Candidate-identifying PII was excluded from AI evaluation.
          </p>
        </Reveal>
      </section>

      {/* ─── CTA ─── */}
      <section style={{ padding: '0 clamp(1.25rem, 4vw, 3rem) clamp(3rem, 6vw, 5rem)', maxWidth: 1100, margin: '0 auto' }}>
        <Reveal>
          <div style={{ position: 'relative', overflow: 'hidden', background: INK, borderRadius: 32, padding: 'clamp(2.5rem, 6vw, 4rem)', textAlign: 'center', color: '#fff' }}>
            <div className="grain-overlay" style={{ opacity: 0.25 }} />
            <Motif shape="star" className="motif-float" style={{ position: 'absolute', top: 26, left: '9%', width: 38, color: TERRA, opacity: 0.9 }} />
            <Motif shape="blob" className="motif-float" style={{ position: 'absolute', bottom: 18, right: '9%', width: 60, color: '#A9DCF2', opacity: 0.5, animationDelay: '1s' }} />
            <div style={{ position: 'relative', zIndex: 1, maxWidth: 620, margin: '0 auto' }}>
              <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: 'clamp(1.9rem, 4.5vw, 2.75rem)', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.08, marginBottom: '0.9rem' }}>Process less. Evaluate better. Hire with confidence.</h2>
              <p style={{ fontSize: '1.0625rem', color: 'rgba(255,255,255,0.7)', marginBottom: '2rem', lineHeight: 1.6 }}>See what CHIRE HIRE can do for your high-volume hiring.</p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '0.85rem', flexWrap: 'wrap' }}>
                <Button size="pill" onClick={() => navigate('/login?mode=register&redirect=payment&plan=starter_monthly')} data-testid="case-cta-start" style={{ background: TERRA, color: '#fff', border: 'none' }}>Subscribe to Starter</Button>
                <Button size="pill" variant="outline" onClick={() => navigate('/pricing')} data-testid="case-cta-pricing" style={{ background: 'transparent', color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}>View Pricing</Button>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <MarketingFooter />
    </div>
  );
}
